import React from "react";
import "./SubStyle.css";

const auditData = {
  label: "BUILT FOR CARE HOMES",
  title: "Audits that lead to",
  highlight: "better care.",
  description:
    "Complete audits, assign actions and monitor compliance across every service—all from one simple platform.",

  features: ["CQC-focused", "Multi-site oversight", "Clear evidence"],

  dashboard: {
    title: "Total Care Audit",
    heading: "Compliance overview",
    percentage: "92%",

    stats: [
      {
        label: "Audits complete",
        value: "24",
      },
      {
        label: "Open actions",
        value: "7",
      },
      {
        label: "Overdue",
        value: "2",
      },
    ],

    audits: [
      {
        title: "Medication audit",
        percentage: "96%",
        status: "Complete",
      },
      {
        title: "Infection control",
        percentage: "90%",
        status: "On track",
      },
      {
        title: "Care plans",
        percentage: "86%",
        status: "Review",
      },
      {
        title: "Health & safety",
        percentage: "94%",
        status: "Complete",
      },
    ],
  },
};
const cards = [
  {
    icon: "✓",
    title: "Care-specific audits",
    description:
      "Complete medication, care plan, infection control, health and safety, call bell and governance audits.",
    color: "purple",
  },
  {
    icon: "◎",
    title: "Live compliance dashboard",
    description:
      "View completion, scores, trends and overdue audits across one home or your whole group.",
    color: "green",
  },
  {
    icon: "↗",
    title: "Action plans",
    description:
      "Assign every improvement to an owner with a priority, deadline and clear evidence of completion.",
    color: "purple",
  },
  {
    icon: "▤",
    title: "Inspection-ready reports",
    description:
      "Produce clear reports that demonstrate governance, learning and continuous improvement.",
    color: "green",
  },
  {
    icon: "⌂",
    title: "Multi-home oversight",
    description:
      "Compare services, identify recurring risks and give senior leaders one reliable view.",
    color: "purple",
  },
  {
    icon: "◷",
    title: "Automated reminders",
    description:
      "Keep audits and actions on schedule with timely notifications for the right team members.",
    color: "green",
  },
];


const steps = [
  {
    number: "1",
    title: "Complete the audit",
    description: "Use structured questions, notes, evidence and scoring.",
  },
  {
    number: "2",
    title: "Assign the action",
    description: "Set responsibility, priority and completion date.",
  },
  {
    number: "3",
    title: "Track and evidence",
    description: "Monitor progress and keep a clear governance trail.",
  },
];




function AuditPage() {
  const { dashboard } = auditData;

  return (
    <>
      <div className="audit-page">
        {/* ================= HERO ================= */}

        <section className="audit-hero">
          <div className="audit-container">
            {/* LEFT SIDE */}
            <div className="audit-content">
              <div className="audit-label">
                <span></span>
                {auditData.label}
              </div>

              <h1>
                {auditData.title}
                <br />
                <span>{auditData.highlight}</span>
              </h1>

              <p>{auditData.description}</p>

              {/* BUTTONS */}
              <div className="audit-actions">
                <a href="#audit-cta" className="audit-button primary">
                  Book a free demo
                  <span>→</span>
                </a>

                <a href="#audit-working" className="audit-button secondary">
                  Explore features
                </a>
              </div>

              {/* FEATURES */}
              <div className="audit-features">
                {auditData.features.map((feature) => (
                  <div className="audit-feature" key={feature}>
                    <span>✓</span>
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT SIDE */}
            <div className="dashboard-area">
              <div className="dashboard">
                {/* TOP BAR */}
                <div className="dashboard-top">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>

                  <strong>{dashboard.title}</strong>
                </div>

                <div className="dashboard-layout">
                  {/* SIDEBAR */}
                  <aside className="dashboard-sidebar">
                    <div className="sidebar-heading">Overview</div>

                    <div className="sidebar-menu active">Dashboard</div>

                    <div className="sidebar-menu">Audits</div>

                    <div className="sidebar-menu">Action plans</div>

                    <div className="sidebar-menu">Reports</div>

                    <div className="sidebar-menu">Locations</div>
                  </aside>

                  {/* MAIN */}
                  <div className="dashboard-content">
                    {/* HEADING */}
                    <div className="dashboard-title">
                      <h2>{dashboard.heading}</h2>

                      <strong>{dashboard.percentage}</strong>
                    </div>

                    {/* STATS */}
                    <div className="dashboard-stats">
                      {dashboard.stats.map((stat) => (
                        <div className="stat" key={stat.label}>
                          <small>{stat.label}</small>

                          <strong>{stat.value}</strong>
                        </div>
                      ))}
                    </div>

                    {/* AUDITS */}
                    <div className="audit-list">
                      {dashboard.audits.map((audit) => (
                        <div className="audit-card" key={audit.title}>
                          <div className="audit-card-top">
                            <strong>{audit.title}</strong>

                            <span>{audit.status}</span>
                          </div>

                          <div className="progress">
                            <div
                              className="progress-value"
                              style={{
                                width: audit.percentage,
                              }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Scroll Down */}
      <div className="scroll-down">
        <div className="mouse">
          <span></span>
        </div>

        <small>SCROLL DOWN</small>
      </div>
      </div>

      <section className="audit-working" id="audit-working">
        <div className="audit-working__container">
          <div className="audit-working__eyebrow">
            <span className="audit-working__line"></span>
            <span>ONE COMPLETE AUDIT SYSTEM</span>
          </div>

          <h1 className="audit-working__title">
            See what is working—and what
            <br className="desktop-break" />
            needs action.
          </h1>

          <p className="audit-working__description">
            Replace spreadsheets and paper files with a consistent, accountable
            audit process
            <br className="desktop-break" />
            across your care homes.
          </p>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="features-section">
        <div className="features-grid">
          {cards.map((card, index) => (
            <article className="feature-card" key={index}>
              <div className={`icon-box ${card.color}`}>
                <span>{card.icon}</span>
              </div>

              <h3>{card.title}</h3>

              <p>{card.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="audit-process">
        {/* Decorative circles */}
        <div className="audit-circle audit-circle-one"></div>
        <div className="audit-circle audit-circle-two"></div>

        <div className="audit-container">
          {/* Left Content */}
          <div className="audit-contents">
            <div className="audit-label">
              <span></span>
              SIMPLE BY DESIGN
            </div>

            <h2>
              From finding a gap to
              <br className="desktop-break" /> proving it is fixed.
            </h2>

            <p>
              Give managers a clear routine and give directors confidence that
              improvements are being completed.
            </p>
          </div>

          {/* Right Steps */}
          <div className="audit-steps">
            {steps.map((step) => (
              <div className="audit-step" key={step.number}>
                <div className="audit-number">{step.number}</div>

                <div className="audit-step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>

                <div className="audit-arrow">→</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="audit-cta" id="audit-cta">
        <div className="audit-cta__content">
          {/* Small heading */}
          <div className="audit-cta__eyebrow">
            <span className="audit-cta__line"></span>
            <span>READY TO SIMPLIFY COMPLIANCE?</span>
          </div>

          {/* Main heading */}
          <h2 className="audit-cta__title">Make every audit count.</h2>

          {/* Description */}
          <p className="audit-cta__description">
            Book a free demonstration and see how Total Care Audit can support
            <br className="desktop-break" />
            safer care and stronger governance.
          </p>

          {/* Button */}
          <button className="audit-cta__button">
            <a
              href="mailto:info@totalcareadvisory.com?subject=Total%20Care%20Rota%20Demo"
            >
              Book Your Free Demo
            </a>
            <span className="audit-cta__arrow">→</span>
          </button>
        </div>
      </section>
    </>
  );
}

export default AuditPage;
